/**
 * The receiving end of an invite link (`D275`): `<base>?invite=<code>` opens
 * the site, and this keeps the code until the person has an account.
 *
 * {@link captureInviteLanding} runs in `main.tsx`'s boot beside the share and
 * plan landings: it keeps the code on the device for 30 days and takes
 * `invite` out of the address. Nothing is sent and nothing opens. After
 * sign-in, `PendingInviteDoor` redeems it once and forgets it — a later
 * account on the same device did not come by that invite. The server credits
 * only an account in its first 14 days, so an old account signing in on a
 * device that kept a code is refused there, silently here.
 *
 * The native shell never boots with `?invite=` (its deep links carry two
 * keys, `D96`, `T-178`).
 */
import {
  INVITE_PARAM,
  type InviteCode,
  inviteCode,
} from "@repo/contract/community";
import { preferences } from "../../native";

export const PENDING_INVITE_KEY = "mypocketgym.share.pending-invite";

/** A landing older than this is not what brought the account. */
export const PENDING_INVITE_TTL_MS = 30 * 86_400_000;

/** `?invite=` of a landing URL's search, when it is a code. */
export function landingInvite(search: string): InviteCode | null {
  const value = new URLSearchParams(search).get(INVITE_PARAM);
  if (value === null) {
    return null;
  }
  const parsed = inviteCode.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/** The search with `invite` taken out, so the router never sees it. */
export function withoutInvite(search: string): string {
  const params = new URLSearchParams(search);
  params.delete(INVITE_PARAM);
  const rest = params.toString();
  return rest === "" ? "" : `?${rest}`;
}

/** The kept landing, if well formed and recent. */
export function livePendingInvite(
  raw: string | null,
  now: number
): InviteCode | null {
  if (raw === null) {
    return null;
  }
  try {
    const value = JSON.parse(raw) as { at?: unknown; code?: unknown };
    const parsed = inviteCode.safeParse(value.code);
    if (
      !parsed.success ||
      typeof value.at !== "number" ||
      now - value.at > PENDING_INVITE_TTL_MS ||
      value.at > now
    ) {
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

export function captureInviteLanding(
  location: Pick<Location, "hash" | "pathname" | "search"> = window.location,
  history: Pick<History, "replaceState" | "state"> = window.history,
  now: () => number = Date.now
): void {
  const code = landingInvite(location.search);
  if (code === null) {
    return;
  }
  history.replaceState(
    history.state,
    "",
    `${location.pathname}${withoutInvite(location.search)}${location.hash}`
  );
  preferences
    .set(PENDING_INVITE_KEY, JSON.stringify({ at: now(), code }))
    .catch(() => undefined);
}

/** The kept code, if live, and forgotten in the same breath. */
export async function takePendingInvite(
  now: number = Date.now()
): Promise<InviteCode | null> {
  const raw = await preferences.get(PENDING_INVITE_KEY).catch(() => null);
  if (raw === null) {
    return null;
  }
  await preferences.remove(PENDING_INVITE_KEY).catch(() => undefined);
  return livePendingInvite(raw, now);
}
